import React, { useState, useEffect, useMemo } from 'react';
import './DateTimePicker.scss';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  DatePicker,
  DatePickerProps,
  DatePickerFieldProps,
} from '@mui/x-date-pickers/DatePicker';
import { PickerValue } from '@mui/x-date-pickers/internals';
import dayjs, { Dayjs } from 'dayjs';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { Clear } from '@mui/icons-material';
import { useRifm } from 'rifm';
import {
  useSplitFieldProps,
  useParsedFormat,
  usePickerContext,
} from '@mui/x-date-pickers/hooks';
import { useValidation, validateDate } from '@mui/x-date-pickers/validation';
import { CalendarIcon } from '@mui/x-date-pickers/icons';
import { formatDateToUtc, parseUtcToLocal, parseRawDateString, formatRawDateString } from 'Utilities/DateTimeUtc';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

interface DateTimePickerInputProps {
  label?: string;
  value?: Date | string; // Can accept Date object or UTC string from backend
  onChange: (e: any) => void;
  required?: boolean;
  error?: string;
  hint?: string;
  dateOnly?: boolean;
  isTable?: boolean;
  minDate?: Date;
}

/**
 * Formats a Date object to UTC string for backend submission
 * @param date - JavaScript Date object
 * @param dateOnly - If true, formats as midnight UTC (for date-only fields)
 * @returns UTC string in "YYYY-MM-DD HH:mm:ss" format
 */
export function formatDate(date: Date, dateOnly: boolean = false) {
  return formatDateToUtc(date, dateOnly);
}

const MASK_USER_INPUT_SYMBOL = '_';
const ACCEPT_REGEX = /[\d]/gi;
const staticDateWith2DigitTokens = dayjs('2019-11-21T11:30:00.000');
const staticDateWith1DigitTokens = dayjs('2019-01-01T09:00:00.000');

function getInputValueFromValue(value: Dayjs | null, format: string) {
  if (value == null) return '';
  return value.isValid() ? value.format(format) : '';
}

function MaskedDateField(props: DatePickerFieldProps) {
  const { internalProps, forwardedProps } = useSplitFieldProps(props, 'date');
  const pickerContext = usePickerContext();
  const parsedFormat = useParsedFormat();

  const [inputValue, setInputValue] = useState<string>(() =>
    getInputValueFromValue(pickerContext.value, pickerContext.fieldFormat)
  );

  useEffect(() => {
    if (pickerContext.value && pickerContext.value.isValid()) {
      const newDisplayDate = getInputValueFromValue(
        pickerContext.value,
        pickerContext.fieldFormat!
      );
      setInputValue(newDisplayDate);
    } else if (!pickerContext.value) {
      setInputValue('');
    }
  }, [pickerContext.fieldFormat, pickerContext.value]);

  const { hasValidationError, getValidationErrorForNewValue } = useValidation({
    value: pickerContext.value,
    timezone: pickerContext.timezone,
    props: internalProps,
    validator: validateDate,
  });

  const handleInputValueChange = (newInputValue: string) => {
    setInputValue(newInputValue);
    const newValue = dayjs(newInputValue, pickerContext.fieldFormat);
    pickerContext.setValue(newValue, {
      validationError: getValidationErrorForNewValue(newValue),
    });
  };

  const rifmFormat = useMemo(() => {
    const formattedDateWith1Digit = staticDateWith1DigitTokens.format(
      pickerContext.fieldFormat
    );
    const inferredFormatPatternWith1Digits = formattedDateWith1Digit.replace(
      ACCEPT_REGEX,
      MASK_USER_INPUT_SYMBOL
    );
    const inferredFormatPatternWith2Digits = staticDateWith2DigitTokens
      .format(pickerContext.fieldFormat)
      .replace(ACCEPT_REGEX, '_');

    if (inferredFormatPatternWith1Digits !== inferredFormatPatternWith2Digits) {
      throw new Error(
        `Mask does not support numbers with variable length such as 'M'.`
      );
    }
    return (valueToFormat: string) => {
      let outputCharIndex = 0;
      return valueToFormat
        .split('')
        .map((character, characterIndex) => {
          ACCEPT_REGEX.lastIndex = 0;
          if (outputCharIndex > inferredFormatPatternWith1Digits.length - 1)
            return '';

          const maskChar = inferredFormatPatternWith1Digits[outputCharIndex];
          const nextMaskChar =
            inferredFormatPatternWith1Digits[outputCharIndex + 1];

          const acceptedChar = ACCEPT_REGEX.test(character) ? character : '';
          const formattedChar =
            maskChar === MASK_USER_INPUT_SYMBOL
              ? acceptedChar
              : maskChar + acceptedChar;

          outputCharIndex += formattedChar.length;

          const isLastCharacter = characterIndex === valueToFormat.length - 1;
          if (
            isLastCharacter &&
            nextMaskChar &&
            nextMaskChar !== MASK_USER_INPUT_SYMBOL
          ) {
            return formattedChar ? formattedChar + nextMaskChar : '';
          }
          return formattedChar;
        })
        .join('');
    };
  }, [pickerContext.fieldFormat]);

  const rifmProps = useRifm({
    value: inputValue,
    onChange: handleInputValueChange,
    format: rifmFormat,
  });

  return (
    <TextField
      {...rifmProps}
      {...forwardedProps}
      placeholder={parsedFormat}
      error={hasValidationError}
      focused={pickerContext.open}
      name={pickerContext.name}
      label={pickerContext.label}
      className={`custom-date-time-picker ${
        hasValidationError ? 'with-error' : ''
      }`}
      sx={{
        width: '100%',
        '& .MuiPickersSectionList-root': { padding: '10px 0px' },
        '& .MuiPickersInputBase-sectionsContainer': { padding: '10px 0px' },
      }}
      InputProps={{
        ref: pickerContext.triggerRef,
        endAdornment: (
          <InputAdornment position="end">
            {pickerContext.value ? (
              <IconButton
                size="small"
                onClick={() => pickerContext.setValue(null)}
                edge="end"
                aria-label="Clear date"
              >
                <Clear fontSize="small" />
              </IconButton>
            ) : (
              <IconButton
                size="small"
                onClick={() => pickerContext.setOpen((prev) => !prev)}
                edge="end"
              >
                <CalendarIcon />
              </IconButton>
            )}
          </InputAdornment>
        ),
      }}
    />
  );
}

function MaskedFieldDatePicker(props: DatePickerProps<any>) {
  return (
    <DatePicker slots={{ ...props.slots, field: MaskedDateField }} {...props} />
  );
}

export const DateTimePickerInput = (props: DateTimePickerInputProps) => {
  const { label, value, onChange, required, error, hint, dateOnly, isTable, minDate } =
    props;

  const minDateValue = minDate ? dayjs(minDate) : undefined;

  // Helper to convert value to Dayjs, handling both Date objects and UTC strings
  const convertValueToDayjs = (val: Date | string | undefined): Dayjs | null => {
    if (!val) return null;

    if (typeof val === 'string') {
      // Date-only fields store a raw calendar date — skip UTC conversion to avoid day shifts
      const parsed = dateOnly ? parseRawDateString(val) : parseUtcToLocal(val);
      return parsed ? dayjs(parsed) : null;
    }

    // If it's a Date object, use it directly (already in local time)
    return dayjs(val);
  };

  const [dateValue, setDateValue] = useState<Dayjs | null>(
    convertValueToDayjs(value)
  );
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Update dateValue when value prop changes (e.g., when form loads data from backend)
  useEffect(() => {
    setDateValue(convertValueToDayjs(value));
  }, [value]);

  const isSafari =
    typeof window !== 'undefined' &&
    /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);

  const onDateChange = (newValue: PickerValue) => {
    if (newValue && dayjs.isDayjs(newValue)) {
      const date = newValue.toDate();
      setDateValue(newValue);
      // Date-only fields preserve the calendar date as-is — no UTC conversion
      onChange(dateOnly ? formatRawDateString(date) : formatDateToUtc(date));
    } else {
      setDateValue(null);
      onChange('');
    }
  };

  const handlePickerOpen = () => setIsPickerOpen(true);
  const handlePickerClose = () => setIsPickerOpen(false);

  return (
    <div className={`date-picker-wrapper${isPickerOpen ? ' picker-open' : ''}`}>
      {label && (
        <span
          className={
            isPickerOpen ? 'date-picker-label-active' : 'date-picker-label'
          }
        >
          {label} {required && <span className="required">*</span>}
        </span>
      )}
      <div
        className={`date-time-picker-row${error ? ' has-external-error' : ''}`}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {dateOnly ? (
            <MaskedFieldDatePicker
              value={dateValue}
              format="MM/DD/YYYY"
              onChange={onDateChange}
              onOpen={handlePickerOpen}
              onClose={handlePickerClose}
              minDate={minDateValue}
              disableOpenPicker={false}
              slotProps={{
                popper:
                  isSafari || !isTable
                    ? { disablePortal: true }
                    : { container: document.body },
              }}
            />
          ) : (
            <DateTimePicker
              value={dateValue}
              ampm
              format="MM/DD/YYYY hh:mm A"
              onChange={onDateChange}
              onOpen={handlePickerOpen}
              onClose={handlePickerClose}
              className={`custom-date-time-picker ${error ? 'with-error' : ''}`}
              minDate={minDateValue}
              disableOpenPicker={false}
              slotProps={{
                popper:
                  isSafari || !isTable
                    ? { disablePortal: true }
                    : { container: document.body },
                textField: {
                  fullWidth: true,
                  sx: {
                    width: '100%',
                    '& .MuiPickersSectionList-root': { padding: '10px 0px' },
                    '& .MuiPickersInputBase-sectionsContainer': {
                      padding: '10px 0px',
                    },
                  },
                  InputProps: {
                    endAdornment: dateValue ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDateValue(null);
                            onChange('');
                          }}
                          edge="end"
                          aria-label="Clear date"
                          id="clear"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                },
              }}
            />
          )}
        </LocalizationProvider>
        {hint && !error && <div className="hint">{hint}</div>}
        {error && <div className="date-picker-error">{error}</div>}
      </div>
    </div>
  );
};
